import { DataPointArray, isDefined } from '@evorait/pace-core';
import { on } from '@ngrx/store';
import { Answers } from '../../../types/answers';
import { CHOICE_TYPE } from '../../../types/choice';
import { ContentId } from '../../../types/content-block';
import { FUNCTIONAL_ELEMENT_TYPE } from '../../../types/functional-element';
import { FunctionalElements } from '../../../types/functional-elements';
import { RADIO_OPTION_TYPE } from '../../../types/radio-option';
import { Signature } from '../../../types/signature';
import { AnswerUtilities } from '../../../utilities/answer-utilities';
import { updateSignature, updateSignatureName } from '../../actions/functional-elements.actions';
import { FormState } from '../../state/form.state';

export function getControllingCheckpointSelection(state: FormState, xmlId: string): RADIO_OPTION_TYPE | undefined {
    const checkpointDependency = state.checkpointDependencies[xmlId];
    if (typeof checkpointDependency === 'undefined') {
        throw new Error(
            `FormReducer: getControllingCheckpointSelection: Could not find dependency for checkpoint with xmlId '${xmlId}'`,
        );
    }
    const controllingCheckpoint = state.checkpoints[checkpointDependency.checkpoint];
    const options = state.participantCopies[state.selectedParticipantCopyId].options[controllingCheckpoint.xmlId];
    if (typeof options === 'undefined') {
        throw new Error(
            `FormReducer: getControllingCheckpointSelection: Could not find options for controlling checkpoint '${controllingCheckpoint.xmlId}'`,
        );
    }

    const selectedOption = options.find((option) => option.selected);
    return typeof selectedOption !== 'undefined' ? selectedOption.optionType : undefined;
}

export function updateSignatureReducer(
    state: Readonly<FormState>,
    props: {
        id: ContentId;
        signatureType: Signature['type'];
        newSignature: string;
        date: string;
        dateXmlId: string;
        signatureDataPoints: DataPointArray;
    },
): Readonly<FormState> {
    // First: Create updated signature entry
    const newSignature: { [key: string]: Readonly<Signature> } = {};
    const oldSignature = state.participantCopies[state.selectedParticipantCopyId].functionalElements[
        props.id
    ] as Signature;
    newSignature[props.id] = {
        ...oldSignature,
        signature: props.newSignature,
        date: props.date,
        signatureDataPoints: [...props.signatureDataPoints],
    };

    const newFunctionalElements: FunctionalElements = {
        ...state.participantCopies[state.selectedParticipantCopyId].functionalElements,
        ...newSignature,
    };

    const newAnswers: Answers = AnswerUtilities.getCompleteAnswers(
        state.selectedParticipantCopyId,
        state.content,
        state.checkpoints,
        state.participantCopies[state.selectedParticipantCopyId].options,
        newFunctionalElements,
        state.checkpointDependencies,
        state.typeFilters,
        state.formFilters,
        state.enableManualIndividualization,
        state.type,
    );

    let isPositiveCheckpointSelected = false;

    // handling cosima choices
    if (oldSignature.choices) {
        const selectedChoice = Object.values(oldSignature.choices).find((choice) => choice?.selected);

        if (isDefined(selectedChoice)) {
            isPositiveCheckpointSelected = selectedChoice.type === CHOICE_TYPE.CHOICE_POSITIVE;
        }
    } else {
        // astoria handling of controlling checkpoints
        const selectedOption = getControllingCheckpointSelection(state, oldSignature.controllingCheckpoint);

        if (isDefined(selectedOption)) {
            isPositiveCheckpointSelected = selectedOption === RADIO_OPTION_TYPE.YES;
        }
    }

    return {
        ...state,
        participantCopies: {
            ...state.participantCopies,
            [state.selectedParticipantCopyId]: {
                ...state.participantCopies[state.selectedParticipantCopyId],
                functionalElements: newFunctionalElements,
                answers: newAnswers,
                signFlag:
                    isPositiveCheckpointSelected &&
                    newSignature[props.id].firstName.length > 0 &&
                    newSignature[props.id].lastName.length > 0 &&
                    newSignature[props.id].signature.length > 0,
            },
        },
    };
}

export function updateSignatureNameReducer(
    state: Readonly<FormState>,
    props: {
        id: ContentId;
        signatureType: Signature['type'];
        xmlId: string;
        firstName: string;
        lastName: string;
        email: string;
    },
): Readonly<FormState> {
    // First: Create updated signature entry
    const newSignature: { [key: string]: Readonly<Signature> } = {};
    const oldSignature = state.participantCopies[state.selectedParticipantCopyId].functionalElements[
        props.id
    ] as Signature;
    newSignature[props.id] = {
        ...oldSignature,
        firstName: props.firstName,
        lastName: props.lastName,
        email: props.email,
    };

    const newFunctionalElements: FunctionalElements = {
        ...state.participantCopies[state.selectedParticipantCopyId].functionalElements,
        ...newSignature,
    };

    const newAnswers: Answers = AnswerUtilities.getCompleteAnswers(
        state.selectedParticipantCopyId,
        state.content,
        state.checkpoints,
        state.participantCopies[state.selectedParticipantCopyId].options,
        newFunctionalElements,
        state.checkpointDependencies,
        state.typeFilters,
        state.formFilters,
        state.enableManualIndividualization,
        state.type,
    );

    let isPositiveCheckpointSelected = false;

    // handling cosima choices
    if (oldSignature.choices) {
        const selectedChoice = Object.values(oldSignature.choices).find((choice) => choice?.selected);

        if (isDefined(selectedChoice)) {
            isPositiveCheckpointSelected = selectedChoice.type === CHOICE_TYPE.CHOICE_POSITIVE;
        }
    } else {
        // astoria handling of controlling checkpoints
        const selectedOption = getControllingCheckpointSelection(state, oldSignature.controllingCheckpoint);

        if (isDefined(selectedOption)) {
            isPositiveCheckpointSelected = selectedOption === RADIO_OPTION_TYPE.YES;
        }
    }

    return {
        ...state,
        participantCopies: {
            ...state.participantCopies,
            [state.selectedParticipantCopyId]: {
                ...state.participantCopies[state.selectedParticipantCopyId],
                functionalElements: newFunctionalElements,
                answers: newAnswers,
                // overwrite participant particulars only for customer signature field updates
                firstName:
                    oldSignature.type === FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_CUSTOMER
                        ? props.firstName
                        : state.participantCopies[state.selectedParticipantCopyId].firstName,
                lastName:
                    oldSignature.type === FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_CUSTOMER
                        ? props.lastName
                        : state.participantCopies[state.selectedParticipantCopyId].lastName,
                email:
                    oldSignature.type === FUNCTIONAL_ELEMENT_TYPE.SIGNOFF_CUSTOMER
                        ? props.email
                        : state.participantCopies[state.selectedParticipantCopyId].email,
                signFlag:
                    isPositiveCheckpointSelected &&
                    newSignature[props.id].firstName.length > 0 &&
                    newSignature[props.id].lastName.length > 0 &&
                    newSignature[props.id].signature.length > 0,
            },
        },
    };
}

export const signatureReducer = [
    on<FormState, Array<typeof updateSignature>>(updateSignature, updateSignatureReducer),
    on<FormState, Array<typeof updateSignatureName>>(updateSignatureName, updateSignatureNameReducer),
];
